import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export type ResultsMode = "tableau" | "consensus" | "engagement" | "blockers";

const MODES: ResultsMode[] = ["tableau", "consensus", "engagement", "blockers"];

export interface UseResultsModeOptions {
  /** Number of slots in the poll — drives the smart default mode */
  slotCount: number;
  /** Number of participants — drives the default consensus threshold */
  participantsCount: number;
}

export interface ResultsModeState {
  mode: ResultsMode;
  /** Threshold for consensus mode (≥1). `Infinity` = "show all" (segmented "∞"). */
  threshold: number;
  setMode: (m: ResultsMode) => void;
  setThreshold: (n: number) => void;
}

export function defaultMode(slotCount: number): ResultsMode {
  // Plan: défaut consensus si > 6 slots, sinon raw.
  return slotCount > 6 ? "consensus" : "tableau";
}

export function defaultThreshold(participantsCount: number): number {
  // Plan: ceil(participants * 0.3), minimum 1.
  return Math.max(1, Math.ceil(participantsCount * 0.3));
}

function parseMode(raw: string | null, fallback: ResultsMode): ResultsMode {
  // Rétro-compat : l'ancien nom "raw" reste interprété comme "tableau".
  if (raw === "raw") return "tableau";
  return raw && (MODES as string[]).includes(raw)
    ? (raw as ResultsMode)
    : fallback;
}

function parseThreshold(raw: string | null, fallback: number): number {
  if (raw === "inf" || raw === "∞") return Number.POSITIVE_INFINITY;
  if (!raw) return fallback;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return Math.floor(n);
}

/**
 * Source de vérité = URL (`?mode=...&threshold=...`). Reload = même écran.
 *
 * Utilise `useSearchParams` de react-router-dom. À utiliser uniquement à
 * l'intérieur d'un `<Router>`.
 */
export function useResultsMode({
  slotCount,
  participantsCount,
}: UseResultsModeOptions): ResultsModeState {
  const [params, setParams] = useSearchParams();
  const fallbackMode = defaultMode(slotCount);
  const fallbackThreshold = defaultThreshold(participantsCount);

  const mode = parseMode(params.get("mode"), fallbackMode);
  const threshold = parseThreshold(params.get("threshold"), fallbackThreshold);

  const setMode = useCallback(
    (m: ResultsMode) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (m === fallbackMode) next.delete("mode");
          else next.set("mode", m);
          return next;
        },
        { replace: true },
      );
    },
    [setParams, fallbackMode],
  );

  const setThreshold = useCallback(
    (n: number) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (n === Number.POSITIVE_INFINITY) next.set("threshold", "inf");
          else if (n === fallbackThreshold) next.delete("threshold");
          else next.set("threshold", String(n));
          return next;
        },
        { replace: true },
      );
    },
    [setParams, fallbackThreshold],
  );

  return { mode, threshold, setMode, setThreshold };
}
